<template>
  <nav
    class="flex items-center justify-center"
    aria-label="Page navigation"
    data-el="pagination"
    :data-pagination-pages="totalPages"
    :data-pagination-current="props.value || props.modelValue"
  >
    <ul
      class="flex h-8 w-auto items-center justify-center -space-x-px rounded-lg text-sm shadow-sm"
    >
      <!-- prev -->
      <li>
        <PaginationBtn
          data-el="pag-prev"
          cap="first"
          :disabled="value === 1"
          @click="decrement"
        >
          <span class="sr-only">Previous</span>
          <svg
            class="h-2.5 w-2.5"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 6 10"
          >
            <path
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M5 1 1 5l4 4"
            />
          </svg>
        </PaginationBtn>
      </li>
      <!-- items -->
      <li v-for="item in firstItems" :key="item">
        <PaginationBtn
          data-el="pag-item-first"
          :data-item="item"
          :active="item === props.value || item === props.modelValue"
          :item="item"
          @click="() => changeTo(item)"
        />
      </li>
      <li v-if="(lastItems && lastItems.length) || isInfinite">
        <PaginationBtn :active="false" disabled>
          <span>...</span>
        </PaginationBtn>
      </li>
      <li v-for="item in lastItems" :key="'page-' + item">
        <PaginationBtn
          :item="item"
          data-el="pag-item-last"
          :data-item="item"
          :active="item === props.value || item === props.modelValue"
          @click="() => changeTo(item || 0)"
        />
      </li>
      <!-- next -->
      <li>
        <PaginationBtn
          cap="last"
          data-el="pag-next"
          type="button"
          @click="increment"
        >
          <span class="sr-only">Next</span>
          <svg
            class="h-2.5 w-2.5"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 6 10"
          >
            <path
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="m1 9 4-4-4-4"
            />
          </svg>
        </PaginationBtn>
      </li>
    </ul>
  </nav>
</template>
<script lang="ts" setup>
import { computed } from 'vue';
import PaginationBtn from './PaginationBtn.vue';

import { getLogger } from '~/lib/services/getLogger';

const log = getLogger('Pagination.vue');
const props = withDefaults(
  defineProps<{
    value?: number | null;
    modelValue?: number | null;
    perPage?: number;
    noCount?: boolean;
    totalRows?: number | null;
  }>(),
  {
    value: undefined,
    modelValue: undefined,
    noCount: false,
    perPage: 10,
    totalRows: undefined,
  },
);
const emit = defineEmits<{
  (e: 'update:modelValue' | 'change', pagerIndex1Based: number): void;
}>();
const changeTo = (i: number) => {
  log('Paginate to ' + i);
  emit('change', i);
  emit('update:modelValue', i);
};
const decrement = () => {
  if (props.value === undefined && props.modelValue === undefined) {
    return;
  }
  const prev = (props.value ?? props.modelValue ?? 0) - 1;
  if (prev >= 1) {
    emit('change', prev);
    emit('update:modelValue', prev);
  }
};

const isInfinite = computed(() => props.totalRows === -1 || props.noCount);

const increment = () => {
  if (props.value === undefined && props.modelValue === undefined) {
    return;
  }
  const next = (props.value ?? props.modelValue ?? 0) + 1;
  emit('change', next);
  emit('update:modelValue', next);
};
const totalPages = computed(() =>
  isInfinite.value
    ? -1
    : props.totalRows
      ? Math.ceil(props.totalRows / props.perPage)
      : 0,
);
const firstItems = computed(() => {
  const x = [];
  const limit = isInfinite.value
    ? 1
    : totalPages.value > 8
      ? 4
      : totalPages.value; // Show only first 4 if totalPages > 8, else show all
  for (let i = 1; i <= limit; i++) {
    x.push(i);
  }
  return x;
});

const lastItems = computed(() => {
  const x = [];
  if (isInfinite.value && (props.value ?? props.modelValue ?? 0) > 1) {
    x.push(props.value ?? props.modelValue ?? 0);
  } else if (totalPages.value > 8) {
    for (let i = totalPages.value - 3; i <= totalPages.value; i++) {
      x.push(i);
    }
  }
  return x;
});
</script>
